import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import api from '../utils/api';
import repairImage1 from '../assets/repair_1.jpeg';
import repairImage2 from '../assets/repair_2.jpeg';
import fabricImage from '../assets/fabric_1.jpeg';
import polishImage from '../assets/polish_1.jpeg';
import './Home.css';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const { data } = await api.get('/products?featured=true');
      setFeaturedProducts(data.slice(0, 6));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading</div>;
  }

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero" style={{
        backgroundImage: 'linear-gradient(rgba(45, 45, 45, 0.7), rgba(45, 45, 45, 0.5)), url(https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Your Home, Curated & Crafted</h1>
            <p className="hero-subtitle">
              Explore our collections, renew your cherished pieces, or design something uniquely yours
            </p>
            <div className="hero-cta">
              <Link to="/products" className="btn btn-primary">
                Shop Collection
              </Link>
              <Link to="/custom-studio" className="btn btn-outline">
                Start Customizing
              </Link>
            </div>
          </div>
        </div>
        <div className="hero-scroll-indicator">
          <span>Scroll to explore</span>
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="value-props">
        <div className="container">
          <div className="props-grid">
            <div className="prop-card">
              <div className="prop-icon">✦</div>
              <h3>Artisan Quality</h3>
              <p>Handcrafted furniture made with traditional techniques and premium materials</p>
            </div>
            <div className="prop-card">
              <div className="prop-icon">♲</div>
              <h3>Sustainable Renewal</h3>
              <p>Restore and revive your beloved pieces with expert repair services</p>
            </div>
            <div className="prop-card">
              <div className="prop-icon">◈</div>
              <h3>Bespoke Design</h3>
              <p>Create furniture tailored to your space, style, and specifications</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-products">
        <div className="container">
          <div className="section-header">
            <h2>Trending Now</h2>
            <p>Discover our most loved pieces</p>
          </div>
          <div className="products-grid">
            {featuredProducts.map(product => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
          <div className="section-cta">
            <Link to="/products" className="btn btn-secondary">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Service Spotlight */}
      <section className="service-spotlight">
        <div className="container">
          <div className="spotlight-grid">
            <div className="spotlight-visual">
              <div className="repair-collage">
                <div className="collage-main">
                  <img 
                    src={polishImage}
                    alt="Furniture restoration main"
                  />
                </div>
                <div className="collage-grid">
                  <div className="collage-item">
                    <img 
                      src={repairImage2}
                      alt="Furniture repair work"
                    />
                  </div>
                  <div className="collage-item">
                    <img 
                      src={fabricImage}
                      alt="Fabric selection"
                    />
                  </div>
                  <div className="collage-item">
                    <img 
                      src={repairImage1}
                      alt="Polish and finishing"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="spotlight-content">
              <h2>Don't Replace. Renew.</h2>
              <p>
                Your furniture holds memories and character. Our expert craftsmen can restore, 
                repair, and rejuvenate your beloved pieces, giving them years of new life.
              </p>
              <ul className="service-list">
                <li>Professional upholstery</li>
                <li>Frame repairs & reinforcement</li>
                <li>Cushion restoration</li>
                <li>Custom fabric selection</li>
              </ul>
              <Link to="/services/repair" className="btn btn-primary">
                Request a Repair
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Studio Invitation */}
      <section className="studio-invitation">
        <div className="container">
          <div className="studio-grid">
            <div className="studio-content">
              <h2>Be the Designer</h2>
              <p>
                Build your dream sofa in our immersive 3D Studio. Choose dimensions, 
                fabrics, colors, and finishes to create furniture that's uniquely yours.
              </p>
              <div className="studio-features">
                <div className="feature-item">
                  <span className="feature-number">01</span>
                  <p>Select your base model</p>
                </div>
                <div className="feature-item">
                  <span className="feature-number">02</span>
                  <p>Customize dimensions & configuration</p>
                </div>
                <div className="feature-item">
                  <span className="feature-number">03</span>
                  <p>Choose fabrics & finishes</p>
                </div>
              </div>
              <Link to="/custom-studio" className="btn btn-primary">
                Enter Studio
              </Link>
            </div>
            <div className="studio-visual">
              <div className="configurator-preview">
                <img 
                  src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80" 
                  alt="Custom furniture configurator"
                  style={{width: '100%', height: '300px', objectFit: 'cover', borderRadius: '8px', marginBottom: 'var(--spacing-md)'}}
                />
                <div className="color-swatches">
                  <span className="swatch" style={{backgroundColor: '#C07A5F'}}></span>
                  <span className="swatch" style={{backgroundColor: '#2F4F4F'}}></span>
                  <span className="swatch" style={{backgroundColor: '#E5DCC3'}}></span>
                  <span className="swatch" style={{backgroundColor: '#8B7355'}}></span>
                </div>
                <p>Endless possibilities await</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shop Gallery */}
      <section className="shop-gallery">
        <div className="container">
          <div className="section-header">
            <h2>Visit Our Showroom</h2>
            <p>Experience our craftsmanship in person</p>
          </div>
          <div className="gallery-grid">
            <div className="gallery-item gallery-large">
              <img 
                src={repairImage1}
                alt="Shop interior view"
              />
              <div className="gallery-overlay">
                <h3>Showroom Display</h3>
              </div>
            </div>
            <div className="gallery-item">
              <img 
                src={fabricImage}
                alt="Fabric selection"
              />
              <div className="gallery-overlay">
                <h3>Premium Fabrics</h3>
              </div>
            </div>
            <div className="gallery-item">
              <img 
                src={repairImage2}
                alt="Workshop area"
              />
              <div className="gallery-overlay">
                <h3>Our Workshop</h3>
              </div>
            </div>
            <div className="gallery-item">
              <img 
                src={polishImage}
                alt="Finishing touches"
              />
              <div className="gallery-overlay">
                <h3>Quality Finishing</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="trust-indicators">
        <div className="container">
          <div className="indicators-grid">
            <div className="indicator">
              <h3>25+</h3>
              <p>Years of Craftsmanship</p>
            </div>
            <div className="indicator">
              <h3>5,000+</h3>
              <p>Happy Customers</p>
            </div>
            <div className="indicator">
              <h3>100%</h3>
              <p>Satisfaction Guarantee</p>
            </div>
            {/* <div className="indicator">
              <h3>Free</h3>
              <p>Delivery & Assembly</p>
            </div> */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
